import React, { useEffect, useMemo } from 'react';
import { Button, MenuItem, Paper, TextField } from '@material-ui/core';
import { Controller, useForm, Control } from 'react-hook-form';
import { PHONE_MAX_LENGTH } from '../../shared/constants';
import { createPhoneChangeHandler } from '../../shared/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { userSettingsValidationSchema } from './validationSchema';
import { ApiError } from '../../shared/api/apiService';
import { useSnackbar } from 'notistack';
import { useGetMe, useUpdateMe } from './hooks';
import { User } from '@app/shared/types';
import {
  FormContainer,
  ModalActions,
  ModalContainer,
  ModalContent,
  ModalTitle,
} from './UserSettingsInfoCard.styles';
import {
  DATE_FORMAT_OPTIONS,
  LOCALE_OPTIONS,
  SelectOption,
  TIME_FORMAT_OPTIONS,
  getTimeZoneOptions,
} from './preferencesOptions';

interface UserSettingsFormData {
  phoneNumber: string;
  companyName?: string;
  locale?: string;
  zoneInfo?: string;
  dateFormat?: string;
  is24HourFormat?: boolean | null;
}

function toOptionalString(value?: string | null): string {
  return value || '';
}

function toNullableString(value?: string): string | null {
  return value && value.length > 0 ? value : null;
}

function mapFormDataToUserPayload(
  formData: UserSettingsFormData,
): Partial<User> {
  const { locale, zoneInfo, dateFormat, is24HourFormat, ...rest } = formData;
  return {
    ...rest,
    locale: toNullableString(locale),
    zoneInfo: toNullableString(zoneInfo),
    dateFormat: toNullableString(dateFormat),
    is24HourFormat: typeof is24HourFormat === 'boolean' ? is24HourFormat : null,
  };
}

const ControlledSelectField = ({
  name,
  label,
  control,
  options,
}: {
  name: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  options: SelectOption<string>[];
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <TextField
        {...field}
        value={field.value || ''}
        select
        variant="outlined"
        label={label}
        InputLabelProps={{ shrink: true }}
        error={!!error}
        helperText={error?.message || 'Optional'}
      >
        <MenuItem value="">Not set</MenuItem>
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    )}
  />
);

export const EditUserModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { control, getValues, setValue, reset, setError, clearErrors } =
    useForm({
      resolver: yupResolver(userSettingsValidationSchema),
    });
  const { data: user } = useGetMe();
  const { mutate: updateUser } = useUpdateMe();
  const { enqueueSnackbar } = useSnackbar();
  const timeZoneOptions = useMemo(getTimeZoneOptions, []);

  const resetFormToUserValues = () => {
    setValue('phoneNumber', user?.phoneNumber || '');
    setValue('companyName', user?.companyName || '');
    setValue('locale', toOptionalString(user?.locale));
    setValue('zoneInfo', toOptionalString(user?.zoneInfo));
    setValue('dateFormat', toOptionalString(user?.dateFormat));
    setValue('is24HourFormat', user?.is24HourFormat ?? null);
  };

  const handleClose = () => {
    reset();
    resetFormToUserValues();
    onClose();
  };

  useEffect(() => {
    resetFormToUserValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onSubmit = async () => {
    clearErrors();
    const newData = mapFormDataToUserPayload(getValues());

    updateUser({ ...user, ...newData } as User, {
      onSuccess: () => {
        enqueueSnackbar('User updated successfully', { variant: 'success' });
        handleClose();
      },
      onError: (error: Partial<ApiError>) => {
        if (error?.data?.errors) {
          const phoneNumberError = error.data.errors.find(
            (err: { field: string; message: string }) =>
              err.field === 'phoneNumber',
          );

          if (phoneNumberError) {
            setError('phoneNumber', {
              type: 'manual',
              message: phoneNumberError.message,
            });
          }
        }

        enqueueSnackbar(error.message || 'Failed to update user', {
          variant: 'error',
        });
      },
    });
  };

  return (
    <ModalContainer open={open} onClose={handleClose}>
      <Paper>
        <ModalContent>
          <ModalTitle>Edit User</ModalTitle>

          <FormContainer>
            {user?.username && (
              <TextField
                variant="outlined"
                label="User Name"
                InputLabelProps={{ shrink: true }}
                value={user.username}
                disabled
              />
            )}
            {user?.email && (
              <TextField
                variant="outlined"
                label="Email"
                InputLabelProps={{ shrink: true }}
                value={user.email}
                disabled
              />
            )}

            <Controller
              name="phoneNumber"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Phone Number"
                  type="tel"
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: PHONE_MAX_LENGTH }}
                  onChange={createPhoneChangeHandler(field.onChange)}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="companyName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Company Name"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <ControlledSelectField
              name="locale"
              label="Language"
              control={control}
              options={LOCALE_OPTIONS}
            />

            <ControlledSelectField
              name="zoneInfo"
              label="Time Zone"
              control={control}
              options={timeZoneOptions}
            />

            <Controller
              name="is24HourFormat"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  value={
                    typeof field.value === 'boolean' ? String(field.value) : ''
                  }
                  onChange={event => {
                    const value = event.target.value;
                    if (value === '') {
                      field.onChange(null);
                      return;
                    }

                    field.onChange(value === 'true');
                  }}
                  select
                  variant="outlined"
                  label="Time Format"
                  placeholder="Time Format"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message || 'Optional'}
                >
                  <MenuItem value="">Not set</MenuItem>
                  {TIME_FORMAT_OPTIONS.map(option => (
                    <MenuItem key={option.label} value={String(option.value)}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <ControlledSelectField
              name="dateFormat"
              label="Date Format"
              control={control}
              options={DATE_FORMAT_OPTIONS}
            />
          </FormContainer>
        </ModalContent>

        <ModalActions>
          <Button variant="text" color="primary" onClick={handleClose}>
            Dismiss
          </Button>
          <Button variant="text" color="primary" onClick={onSubmit}>
            Save Changes
          </Button>
        </ModalActions>
      </Paper>
    </ModalContainer>
  );
};
